import React from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

export const LENS_EDIT_BAR_HEIGHT = 40;

interface LensEditBarProps {
  hasUnsavedChanges: boolean;
  onDiscard: () => void;
  onDone: () => void;
}

/**
 * The one cue that the board is in edit mode: an Ocean Teal bar over the board column, above
 * the Lens title. It names the mode, says whether a draft is waiting, and holds Discard and
 * Done editing. The chat pane carries no edit chrome of its own.
 */
export default function LensEditBar({ hasUnsavedChanges, onDiscard, onDone }: LensEditBarProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const onBar = tokens.accentContrast;
  return (
    <Stack
      alignItems="center"
      direction="row"
      spacing={1.5}
      data-testid="lens-edit-bar"
      sx={{
        bgcolor: tokens.band,
        color: onBar,
        height: LENS_EDIT_BAR_HEIGHT,
        px: { sm: 3, xs: 2 },
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'inherit', whiteSpace: 'nowrap' }}>Editing</Typography>
      <Typography sx={{ fontSize: 13.5, color: alpha(onBar, 0.78), whiteSpace: 'nowrap', minWidth: 0 }} noWrap>
        {hasUnsavedChanges ? 'Draft · unsaved changes' : 'No unsaved changes'}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Button
        disabled={!hasUnsavedChanges}
        onClick={onDiscard}
        size="small"
        sx={{
          color: onBar,
          borderColor: alpha(onBar, 0.55),
          minHeight: 28,
          textTransform: 'none',
          '&.Mui-disabled': { color: alpha(onBar, 0.4), borderColor: alpha(onBar, 0.25) },
        }}
        variant="outlined"
      >
        Discard
      </Button>
      <Button
        onClick={onDone}
        size="small"
        sx={{
          bgcolor: onBar,
          color: tokens.band,
          minHeight: 28,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { bgcolor: alpha(onBar, 0.9) },
        }}
        variant="contained"
      >
        Done editing
      </Button>
    </Stack>
  );
}
